import { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL, fmt, calcPricing } from '../../utils';
import { VipTermsModal } from '../VipTermsModal';
import { PrivacyModal } from '../PrivacyModal';
import { useLang } from '../../i18n';

let _stripeInstance = null;
let _stripeLoading = null;

function getStripe(publishableKey) {
  if (_stripeInstance) return Promise.resolve(_stripeInstance);
  if (_stripeLoading) return _stripeLoading;
  _stripeLoading = new Promise((resolve) => {
    const init = () => { _stripeInstance = window.Stripe(publishableKey); resolve(_stripeInstance); };
    if (window.Stripe) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.onload = init;
    document.head.appendChild(s);
  });
  return _stripeLoading;
}

/**
 * VIP Day Pass checkout (LR-125-F1). A slot-less, full-day paid pass: no time-slot,
 * guest, bundle or promo selection. Reuses the day-pass Stripe pieces (getStripe,
 * PaymentElement, confirmPayment) and the shared /api/create-payment-intent +
 * /api/confirm-reservation endpoints. Price, service fee and access window are resolved
 * and locked server-side; the client only renders and pays the server-authoritative total.
 */
export function VipCheckoutModal({ vipInfo, date, dayInfo, config, onClose, onComplete, trackEvent }) {
  const { t, lang, monthNames, dayNames } = useLang();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('form');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [bookingRef, setBookingRef] = useState('');
  const [bookingToken, setBookingToken] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [paying, setPaying] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [stripeObj, setStripeObj] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const paymentRef = useRef(null);
  const payingRef = useRef(false);

  const cancelCutoffHours = Number(vipInfo?.cancellationCutoffHours) > 0 ? Number(vipInfo.cancellationCutoffHours) : 24;
  const unitPrice = Number(dayInfo?.price) || 0;
  const pricing = calcPricing(unitPrice, 1, vipInfo?.taxRate, vipInfo?.serviceFeeRate);
  const amountCents = Math.round(pricing.total * 100);

  const dateObj = new Date(date + 'T12:00:00');
  const dateLabel = `${dayNames[dateObj.getDay()]}, ${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}`;
  const accessLine = t('vipAccessWindow', { window: dayInfo?.accessWindow || '' });

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    if (!config.stripePublishableKey) return;
    getStripe(config.stripePublishableKey).then((s) => setStripeObj(s));
  }, [config.stripePublishableKey]);

  useEffect(() => {
    if (!stripeObj || !paymentRef.current || elements) return;
    const elems = stripeObj.elements({
      mode: 'payment',
      amount: amountCents > 0 ? amountCents : 100,
      currency: 'usd',
      paymentMethodTypes: ['card'],
      // Must match the PaymentIntent created by /api/create-payment-intent — Stripe refuses
      // to confirm when the element and the intent disagree on setup_future_usage.
      setupFutureUsage: 'off_session',
      appearance: { theme: 'stripe' },
    });
    // Stripe would print its own card-on-file consent sentence above ours because of
    // setup_future_usage; ours is the one the client approved, so suppress Stripe's.
    const pe = elems.create('payment', { terms: { card: 'never' } });
    pe.mount(paymentRef.current);
    pe.on('ready', () => setPaymentReady(true));
    setElements(elems);
  }, [stripeObj, amountCents, elements]);

  useEffect(() => {
    if (!elements || amountCents <= 0) return;
    elements.update({ amount: amountCents });
  }, [elements, amountCents]);

  const handlePay = useCallback(async () => {
    if (payingRef.current) return;
    payingRef.current = true;

    if (!name.trim() || !email.trim() || !phone.replace(/\D/g, '') || !termsAgreed) {
      setAttempted(true);
      if (!termsAgreed) setTermsError('Please agree to the Terms & Conditions to continue');
      payingRef.current = false;
      return;
    }
    if (!stripeObj || !elements || !paymentReady) {
      payingRef.current = false;
      return;
    }
    setAttempted(false);
    setTermsError('');
    setPaying(true);
    setBookingError('');

    // Step 1: Validate card form (no charge yet)
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaying(false);
      setBookingError(submitError.message);
      payingRef.current = false;
      return;
    }

    // Step 2: Reserve the VIP pass — daily capacity + price/window locked server-side
    let reserveData;
    try {
      const reserveRes = await fetch(`${BACKEND_URL}/api/vip/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          name: name.trim(),
          email: email.trim(),
          phone: phone.replace(/\D/g, '') || '',
          termsAgreed: true,
        }),
      });
      reserveData = await reserveRes.json();
      if (!reserveRes.ok) {
        setPaying(false);
        setBookingError(reserveData.detail || t('slotNoLongerAvailable'));
        payingRef.current = false;
        return;
      }
    } catch (e) {
      setPaying(false);
      setBookingError(t('networkError'));
      payingRef.current = false;
      return;
    }

    const { ref, releaseToken, serverTotal } = reserveData;
    setBookingRef(ref);

    const release = () => fetch(`${BACKEND_URL}/api/release/${ref}/${releaseToken}`, { method: 'POST' }).catch(() => {});

    // Step 3: Create PaymentIntent from the server-calculated total
    const chargeAmountCents = Math.round((serverTotal || pricing.total) * 100);
    let piData;
    try {
      const piRes = await fetch(`${BACKEND_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: chargeAmountCents,
          booking_ref: ref,
          customer_email: email.trim(),
          customer_name: name.trim(),
          description: `Las Ruinas VIP Day Pass - ${dateLabel}`,
        }),
      });
      piData = await piRes.json();
      if (!piData.client_secret) {
        await release();
        setPaying(false);
        setBookingError(piData.detail || t('failedPayment'));
        payingRef.current = false;
        return;
      }
    } catch (e) {
      await release();
      setPaying(false);
      setBookingError(t('networkError'));
      payingRef.current = false;
      return;
    }

    // Step 4: Charge the card
    const { error, paymentIntent } = await stripeObj.confirmPayment({
      elements,
      clientSecret: piData.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      await release();
      setPaying(false);
      setBookingError(error.message);
      payingRef.current = false;
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Step 5: Confirm the reservation as paid (webhook is the backup path)
      try {
        const confirmRes = await fetch(`${BACKEND_URL}/api/confirm-reservation/${ref}/${releaseToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            termsAgreed: true,
            termsAgreedAt: new Date().toISOString(),
            // 1.1 — LR-155 añadió la cláusula de re-entrada / pulsera al punto 4.3. El Terms Log
            // guarda esta versión, así que subirla es lo que permite distinguir quién aceptó el
            // texto con esa cláusula y quién el anterior.
            termsVersion: '1.1',
          }),
        });
        const confirmData = await confirmRes.json();
        if (confirmData.rescheduleToken) setBookingToken(confirmData.rescheduleToken);
      } catch (e) {
        // Non-fatal: webhook will confirm
      }

      try { if (onComplete) await onComplete(); } catch (e) {}
      if (trackEvent) trackEvent('purchase', { value: serverTotal || pricing.total, ref });
      setStep('success');
      setEmailStatus('sent');
      payingRef.current = false;
    }
  }, [stripeObj, elements, paymentReady, name, email, phone, termsAgreed, date, dateLabel, pricing.total, onComplete, trackEvent, t]);

  if (step === 'success') {
    const confirmationUrl = bookingToken ? `/booking/${bookingRef}/${bookingToken}` : '';
    return (
      <div className="lr-overlay" data-testid="vip-checkout-success">
        <div className="lr-modal">
          <button className="close-btn" data-testid="close-vip-success" onClick={onClose}>&times;</button>
          <div className="lr-success">
            <div className="sparkle">{'✨'}</div>
            <h3>{t('youreConfirmed')}</h3>
            <p style={{ color: 'var(--bark)', fontSize: 13, marginBottom: 12 }}>{t('vipBooked')}</p>
            <div className="detail">
              {dateLabel}<br />
              {accessLine}<br />
              {fmt(pricing.total)}
            </div>
            <div className="ref-badge" data-testid="vip-booking-ref">{bookingRef}</div>
            <div className={`lr-email-status ${emailStatus}`} data-testid="vip-email-status">
              {emailStatus === 'sent' && <>{'✓'} {t('emailSent')}</>}
            </div>
            {confirmationUrl && (
              <a href={confirmationUrl} className="lr-view-booking-btn" data-testid="view-my-vip-booking-btn">
                {t('viewMyBooking')}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="lr-overlay" data-testid="vip-checkout-modal">
        <div className="lr-modal" style={{ position: 'relative' }}>
          {paying && <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'wait', borderRadius: 'inherit' }} />}
          <button className="close-btn" data-testid="close-vip-checkout" onClick={onClose} disabled={paying}>&times;</button>
          <h3>{t('vipCheckout')}</h3>

          <div data-testid="vip-checkout-info" style={{
            textAlign: 'center', fontSize: 13, color: '#4A4438',
            marginBottom: 10, fontWeight: 500, background: '#F5F0E8',
            padding: '8px 12px', borderRadius: 6,
          }}>
            {dateLabel}<br />
            <span style={{ fontSize: 12, color: '#8A7E6E' }}>{accessLine}</span>
          </div>

          {bookingError && <div className="lr-promo-error" data-testid="vip-booking-error" style={{ marginBottom: 12 }}>{bookingError}</div>}

          <div className="lr-section">
            <div className="lr-section-title">{t('orderSummary')}</div>
            <div className="lr-summary-row"><span>{t('vipDayPassX1')}</span><span>{fmt(pricing.subtotal)}</span></div>
            <div className="lr-summary-row"><span>{t('serviceFee')} ({vipInfo.serviceFeeRate}%)</span><span>{fmt(pricing.fee)}</span></div>
            <div className="lr-summary-row"><span>{t('tax')} ({vipInfo.taxRate}%)</span><span>{fmt(pricing.tax)}</span></div>
            <div className="lr-summary-row total"><span>{t('total')}</span><span>{fmt(pricing.total)}</span></div>
          </div>

          <div className="lr-section">
            <div className="lr-section-title">{t('contact')}</div>
            <div className="lr-field">
              <label className="lr-label">{t('fullName')}</label>
              <input className="lr-input" data-testid="vip-contact-name" value={name} onChange={e => { setName(e.target.value); setAttempted(false); }} placeholder="Maria Santos" style={attempted && !name.trim() ? { borderColor: 'var(--red)' } : {}} />
              {attempted && !name.trim() && <div className="lr-promo-error" data-testid="vip-name-error" style={{ marginTop: 4 }}>{t('nameRequired')}</div>}
            </div>
            <div className="lr-field">
              <label className="lr-label">{t('email')}</label>
              <input className="lr-input" data-testid="vip-contact-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setAttempted(false); }} placeholder="maria@example.com" style={attempted && !email.trim() ? { borderColor: 'var(--red)' } : {}} />
              {attempted && !email.trim() && <div className="lr-promo-error" data-testid="vip-email-error" style={{ marginTop: 4 }}>{t('emailRequired')}</div>}
            </div>
            <div className="lr-field">
              <label className="lr-label">{t('phoneNumber')}</label>
              <input className="lr-input" data-testid="vip-contact-phone" type="tel" value={phone} onChange={e => {
                const digits = e.target.value.replace(/\D/g, '');
                let formatted = '';
                if (digits.length === 0) formatted = '';
                else if (digits.length <= 1) formatted = '+' + digits;
                else if (digits.length <= 4) formatted = '+' + digits[0] + ' (' + digits.slice(1);
                else if (digits.length <= 7) formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4);
                else formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7, 11);
                setPhone(formatted);
                setAttempted(false);
              }} placeholder="+1 (787) 000-0000" style={attempted && !phone.replace(/\D/g, '') ? { borderColor: 'var(--red)' } : {}} />
              {attempted && !phone.replace(/\D/g, '') && <div className="lr-promo-error" data-testid="vip-phone-error" style={{ marginTop: 4 }}>{t('phoneRequired')}</div>}
            </div>
          </div>

          <div className="lr-section">
            <div className="lr-terms-row" data-testid="vip-terms-checkbox-row">
              <label className="lr-terms-label">
                <input type="checkbox" className="lr-terms-check" data-testid="vip-terms-checkbox" checked={termsAgreed} onChange={e => { setTermsAgreed(e.target.checked); if (e.target.checked) setTermsError(''); }} />
                <span className="lr-terms-text">
                  {name.trim() ? <>{t('iPersonAgree', { name: name.trim() })} </> : <>{t('iAgree')} </>}
                  <button type="button" className="lr-terms-link" data-testid="vip-terms-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}>{t('termsAndConditions')}</button>
                  {' '}{t('andConnector')}{' '}
                  <button type="button" className="lr-terms-link" data-testid="vip-privacy-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}>{t('privacyPolicy')}</button>
                </span>
              </label>
            </div>
            {termsError && <div className="lr-promo-error" data-testid="vip-terms-error" style={{ marginTop: 6 }}>{termsError}</div>}
          </div>

          {/* Cancellation Policy (LR-126-F1) */}
          <div data-testid="vip-cancellation-policy-box" style={{
            margin: '0 0 16px', padding: '14px 16px', background: '#FAFAF8',
            border: '1px solid #e8e4df', borderRadius: 8, fontSize: 12,
            lineHeight: 1.7, color: 'var(--bark)', fontFamily: 'Jost, sans-serif',
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('cancellationPolicy')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#2D7A4F' }}>&#10003;</span> {t('vipCancelPolicyFree', { hours: cancelCutoffHours })}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('vipCancelPolicyLate', { hours: cancelCutoffHours })}</div>
            <div style={{ fontSize: 11, color: '#8B7E74', marginTop: 6 }}>{t('stripeNonRefundable')}</div>
          </div>

          <div className="lr-section">
            <div className="lr-section-title">{t('payment')}</div>
            <div ref={paymentRef} data-testid="vip-stripe-payment-element" style={{ minHeight: 100, marginBottom: 4 }}></div>
            <div className="lr-stripe-badge" data-testid="vip-stripe-secured-badge">&#128274; {t('paymentsSecuredBy')}</div>
            <div data-testid="vip-card-on-file-consent" style={{ fontSize: 11, color: '#8B7E74', lineHeight: 1.5, margin: '6px 0 8px' }}>{t('cardOnFileConsent')}</div>
            <button className="lr-pay-full primary" data-testid="vip-pay-button" disabled={!paymentReady || paying || !termsAgreed} onClick={handlePay}>
              {paying ? t('processing') : `${t('pay')} ${fmt(pricing.total)}`}
            </button>
          </div>
        </div>
      </div>
      {showTermsModal && (
        <VipTermsModal
          onAgree={() => { setTermsAgreed(true); setTermsError(''); setShowTermsModal(false); }}
          onClose={() => setShowTermsModal(false)}
        />
      )}
      {showPrivacyModal && (
        <PrivacyModal onClose={() => setShowPrivacyModal(false)} />
      )}
    </>
  );
}
